import { useEffect, useRef } from 'react'

interface ProtectedSignatureProps {
  src: string
  alt: string
}

export default function ProtectedSignature({ src, alt }: ProtectedSignatureProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    // Fetch image as blob to avoid exposing URL
    fetch(src)
      .then(res => res.blob())
      .then(blob => {
        const img = new Image()
        const objectUrl = URL.createObjectURL(blob)

        img.onload = () => {
          // Calculate dimensions to fit within 150x60
          const maxWidth = 150
          const maxHeight = 60

          let width = img.width
          let height = img.height

          if (width > maxWidth) {
            height = (height * maxWidth) / width
            width = maxWidth
          }

          if (height > maxHeight) {
            width = (width * maxHeight) / height
            height = maxHeight
          }

          canvas.width = width
          canvas.height = height

          ctx.drawImage(img, 0, 0, width, height)

          // Revoke object URL immediately after drawing
          URL.revokeObjectURL(objectUrl)
        }

        img.src = objectUrl
      })
      .catch(() => {
        // Draw placeholder on error
        canvas.width = 100
        canvas.height = 40
        ctx.fillStyle = '#f3f4f6'
        ctx.fillRect(0, 0, 100, 40)
        ctx.fillStyle = '#9ca3af'
        ctx.font = '10px sans-serif'
        ctx.textAlign = 'center'
        ctx.fillText('No signature', 50, 25)
      })
  }, [src])

  // Prevent all forms of copying/saving
  const blockEvent = (e: React.SyntheticEvent) => {
    e.preventDefault()
    e.stopPropagation()
    return false
  }

  return (
    <div
      ref={containerRef}
      className="signature-protected"
      onContextMenu={blockEvent}
      onDragStart={blockEvent}
      onCopy={blockEvent}
      title={alt}
    >
      <canvas ref={canvasRef} role="img" aria-label={alt} />
      {/* Invisible overlay to intercept all interactions */}
      <div className="signature-overlay" />
    </div>
  )
}
